import { useLayoutEffect, useRef, type MouseEvent } from 'react'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { NavItem } from '@/deskUiSkeletons/Menu/NavItem'
import { useSharedSurface } from '@/deskUiSkeletons/Shared/SharedSurfaceContext'
import { useSubsStore } from '@/state/MsgTreeProvider'

// Folder menu entry (Bookmarks/Drafts/Basket) with the hover section flyout:
// hovering the tab reveals Answers/Msges sub-buttons visually fused with it —
// the tab drops its bottom rounding, the flyout drops its top rounding, and a
// sub-button opens the folder directly on that section. Without sections
// (answer-desk drafts/basket) it degrades to the plain shared NavItem.
// Shared styled look (Layer C-shared); the neutral skeleton keeps only
// NavItem. Surface slots opt into this complete folder treatment.

const FLYOUT_CLASS = 'md-sidebar__nav-flyout absolute left-0 top-full z-[100000] hidden w-max min-w-full flex-col overflow-hidden rounded-b-[9px] bg-[#e9effd] shadow-[0_10px_30px_rgba(15,23,42,0.14)] group-hover:flex'
const SECTION_BUTTON_CLASS = 'md-sidebar__nav-flyout-btn flex h-[32px] w-full cursor-pointer items-center gap-1.5 whitespace-nowrap border-0 bg-transparent px-3 text-left text-[13px] font-medium leading-none text-[#5b6675] transition-colors duration-150 hover:bg-[#2563eb33] hover:text-[#2563eb]'
const SECTION_COUNT_CLASS = 'text-[11px] text-[rgba(0,0,0,0.4)]'

export function FolderNavItem({
  label,
  className,
  mode,
  isActive = false,
  isLoading = false,
  loadingCount = 0,
  withSections = true,
  showEmptySections = true,
  sectionLabels,
  sectionCounts = [0, 0],
}: {
  label: string
  className: string
  /** One of the folder modes: 'favs' | 'drafts' | 'trash'. */
  mode: DeskViewMode
  isActive?: boolean
  isLoading?: boolean
  loadingCount?: number
  /** false (answer-desk drafts/basket): plain tab, no section flyout. */
  withSections?: boolean
  /** Surface policy: whether a zero-count section remains in the flyout. */
  showEmptySections?: boolean
  /** Surface vocabulary for the [answers, messages] flyout entries. */
  sectionLabels: readonly [string, string]
  /** [answers, msges] counts shown on the flyout sub-buttons. */
  sectionCounts?: [number, number]
}) {
  const { skin } = useSharedSurface()
  const { state } = useSubsStore()
  const itemRef = useRef<HTMLLIElement | null>(null)
  const idleWidth = useRef<number | null>(null)

  // Same idle-width pinning as the shared NavItem: the loading "+N" label swap
  // must not resize the tab (see NavItem.tsx for the full rationale).
  useLayoutEffect(() => {
    if (!isLoading && itemRef.current) idleWidth.current = itemRef.current.getBoundingClientRect().width
  })

  if (!withSections) {
    return (
      <NavItem
        label={label}
        className={className}
        mode={mode}
        isActive={isActive}
        isLoading={isLoading}
        loadingCount={loadingCount}
      />
    )
  }

  const activeSection = state.renderProjection.activeSection

  // BEHAVIOUR: both paths go through the semantic desk-navigation pipeline.
  function handleTabClick(event: MouseEvent<HTMLElement>) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  function handleSectionClick(event: MouseEvent<HTMLButtonElement>, section: 0 | 1) {
    event.preventDefault()
    event.stopPropagation()
    dispatchDeskNavigation({ mode, section, target: event.currentTarget })
  }

  function sectionButtonClass(section: 0 | 1) {
    const isCurrent = isActive && activeSection === (section === 1 ? 'submsges' : 'answers')
    return `${SECTION_BUTTON_CLASS}${isCurrent ? ' font-semibold text-[#2563eb]' : ''}`
  }

  return (
    <li
      ref={itemRef}
      className={[
        className,
        skin.navItem,
        'hover:rounded-b-none',
        isActive ? `md-sidebar__nav-item--selected ${skin.navItemSelected}` : '',
        isLoading ? `md-sidebar__nav-item--loading ${skin.navLoading}` : '',
      ].filter(Boolean).join(' ')}
      style={isLoading && idleWidth.current ? { width: idleWidth.current } : undefined}
      onClick={handleTabClick}
    >
      <div className={`md-sidebar__nav-label ${skin.navLabel}`}>
        {isLoading ? `+${loadingCount}` : label}
      </div>
      <div className={FLYOUT_CLASS}>
        {showEmptySections || sectionCounts[0] > 0 ? (
          <button type="button" className={sectionButtonClass(0)} onClick={(event) => handleSectionClick(event, 0)}>
            {sectionLabels[0]}
            <span className={SECTION_COUNT_CLASS}>({sectionCounts[0]})</span>
          </button>
        ) : null}
        {showEmptySections || sectionCounts[1] > 0 ? (
          <button type="button" className={sectionButtonClass(1)} onClick={(event) => handleSectionClick(event, 1)}>
            {sectionLabels[1]}
            <span className={SECTION_COUNT_CLASS}>({sectionCounts[1]})</span>
          </button>
        ) : null}
      </div>
    </li>
  )
}
